import type {
    MembershipIncome,
    MembershipIncomeUpsertDto,
} from '@fruitful/member-profile';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToastController } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import type { MembershipDetails } from '../../../entities/entityTypes';
import { captureException } from '../../../logger/Logger';
import {
    formFromIncome,
    type IncomeDetailsFormState,
    type OwnerPickerItem,
} from './moneyMapperIncomeForm';
import {
    incomeFormValidationModalToastMessage,
    persistMembershipIncomeFromForm,
    validateIncomeFormForUpsert,
} from './moneyMapperIncomePersist';

type UpsertFn = (
    membershipId: string,
    body: MembershipIncomeUpsertDto[],
) => Promise<unknown>;

export const useMoneyMapperIncomeDetailsModal = ({
    membership,
    incomes,
    isSaving,
    canMutateIncomes,
    ownerPickerItems,
    upsertMembershipIncomeAsync,
    deleteMembershipIncomeAsync,
    refetchMembershipAndSyncMoneyMap,
}: {
    membership: MembershipDetails | null;
    incomes: MembershipIncome[];
    isSaving: boolean;
    canMutateIncomes: boolean;
    ownerPickerItems: OwnerPickerItem[];
    upsertMembershipIncomeAsync: UpsertFn;
    deleteMembershipIncomeAsync: (incomeId: string) => Promise<unknown>;
    refetchMembershipAndSyncMoneyMap: () => Promise<void>;
}) => {
    const toast = useToastController();

    const [detailsIncomeId, setDetailsIncomeId] = useState<string | null>(null);
    const [detailsModalDraft, setDetailsModalDraft] =
        useState<IncomeDetailsFormState | null>(null);
    const [isSubmittingIncome, setIsSubmittingIncome] = useState(false);

    const detailsIncome = useMemo(() => {
        if (detailsIncomeId == null) {
            return null;
        }
        return incomes.find((i) => i.id === detailsIncomeId) ?? null;
    }, [detailsIncomeId, incomes]);

    useEffect(() => {
        if (detailsIncome == null) {
            setDetailsModalDraft(null);
            return;
        }
        setDetailsModalDraft(formFromIncome(detailsIncome));
    }, [detailsIncomeId, detailsIncome?.id, detailsIncome?.updatedAt]);

    const closeDetailsModal = useCallback(() => {
        setDetailsIncomeId(null);
    }, []);

    const detailsModalDisabled =
        !canMutateIncomes ||
        isSubmittingIncome ||
        isSaving ||
        ownerPickerItems.length === 0;

    const handleSaveIncomeDetails = useCallback(async () => {
        if (
            membership == null ||
            detailsIncome == null ||
            detailsModalDraft == null
        ) {
            return;
        }

        const validation = validateIncomeFormForUpsert(detailsModalDraft);
        if (!validation.ok) {
            toast.show(
                incomeFormValidationModalToastMessage[validation.reason],
                MutationErrorToastOptions(),
            );
            return;
        }

        setIsSubmittingIncome(true);
        try {
            await persistMembershipIncomeFromForm({
                upsertMembershipIncomeAsync,
                refetchMembershipAndSyncMoneyMap,
                membershipId: membership.id,
                existing: detailsIncome,
                form: detailsModalDraft,
                dtoOverrides: {
                    additionalInformation:
                        detailsModalDraft.notes.trim().length > 0
                            ? detailsModalDraft.notes.trim()
                            : '',
                },
            });
            toast.show('Income saved', MutationSuccessToastOptions);
            closeDetailsModal();
        } catch (error) {
            captureException(error);
            toast.show('Error saving income', MutationErrorToastOptions(error));
        } finally {
            setIsSubmittingIncome(false);
        }
    }, [
        closeDetailsModal,
        detailsIncome,
        detailsModalDraft,
        membership,
        refetchMembershipAndSyncMoneyMap,
        toast,
        upsertMembershipIncomeAsync,
    ]);

    const handleDeleteIncome = useCallback(async () => {
        if (membership == null || detailsIncome == null) {
            return;
        }
        setIsSubmittingIncome(true);
        try {
            await deleteMembershipIncomeAsync(detailsIncome.id);
            closeDetailsModal();
            await refetchMembershipAndSyncMoneyMap();
            toast.show('Income deleted', MutationSuccessToastOptions);
        } catch (error) {
            captureException(error);
            toast.show(
                'Error deleting income',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsSubmittingIncome(false);
        }
    }, [
        closeDetailsModal,
        deleteMembershipIncomeAsync,
        detailsIncome,
        membership,
        refetchMembershipAndSyncMoneyMap,
        toast,
    ]);

    const detailsDescriptionTrimmed = detailsIncome?.description.trim() ?? '';
    const detailsModalTitle =
        detailsDescriptionTrimmed.length > 0
            ? detailsDescriptionTrimmed
            : 'Details';

    return {
        detailsIncomeId,
        setDetailsIncomeId,
        closeDetailsModal,
        detailsIncome,
        detailsModalDraft,
        setDetailsModalDraft,
        detailsModalDisabled,
        detailsModalTitle,
        handleSaveIncomeDetails,
        handleDeleteIncome,
        isSubmittingIncome,
    };
};
